import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useInView, useReducedMotion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Check, ArrowRight, Download, Loader2 } from "lucide-react";

const STEPS = [
  "Upload received",
  "Transcribing audio",
  "Identifying speakers",
  "Detecting communication patterns",
  "Evaluating consistency",
  "Measuring communication confidence",
  "Generating insights",
  "Building report",
];

const CONVERSATION = [
  { s: "Emily", t: "Did you get a chance to call the contractor yesterday?" },
  { s: "Ryan", t: "Yeah... I talked to him yesterday afternoon." },
  { s: "Emily", t: "That's good. I thought you said this morning that you hadn't spoken with him yet." },
  { s: "Ryan", t: "Oh... I meant I hadn't spoken with him today. Yesterday I just left him a voicemail... actually... no... wait... he did answer for a few minutes." },
];

const METRICS = [
  ["Communication Clarity", "High"],
  ["Timeline Consistency", "Moderate Variance"],
  ["Emotional State", "Calm"],
  ["Communication Style", "Cooperative"],
];

const PATTERNS = [
  "Calm emotional presentation",
  "Self-correction detected",
  "Cooperative communication",
  "Low emotional escalation",
  "Consistent conversational tone",
];

const QUESTIONS = [
  "Can you walk me through exactly what happened yesterday?",
  "When did the contractor answer?",
  "What did you discuss?",
];

const STEP_MS = 650;

const fade = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.5 },
};

export function GlexisInAction({ primaryTo }) {
  const ref = useRef(null);
  const inView = useInView(ref, { amount: 0.3, once: false });
  const prefersReduced = useReducedMotion();
  const [visible, setVisible] = useState(0);
  const [done, setDone] = useState(false);
  const timers = useRef([]);

  useEffect(() => {
    const clearAll = () => {
      timers.current.forEach((t) => clearTimeout(t));
      timers.current = [];
    };

    if (!inView) {
      clearAll();
      setVisible(0);
      setDone(false);
      return;
    }

    if (prefersReduced) {
      setVisible(STEPS.length);
      setDone(true);
      return;
    }

    clearAll();
    setVisible(0);
    setDone(false);
    STEPS.forEach((_, i) => {
      timers.current.push(setTimeout(() => setVisible(i + 1), STEP_MS * (i + 1)));
    });
    timers.current.push(setTimeout(() => setDone(true), STEP_MS * STEPS.length + 600));

    return clearAll;
  }, [inView, prefersReduced]);

  return (
    <section id="demo" ref={ref} className="border-t border-slate-800/60 py-24">
      <div className="mx-auto max-w-6xl px-4 sm:px-8">
        <motion.div {...fade} className="mx-auto max-w-2xl text-center">
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-cyan-400">Interactive Demo</span>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight text-white">See Glexis in Action</h2>
          <p className="mt-4 text-slate-400">
            Experience how Glexis transforms an everyday conversation into meaningful communication insights.
          </p>
        </motion.div>

        <div className="mt-14 grid gap-6 lg:grid-cols-2">
          {/* Left — Conversation */}
          <motion.div {...fade} className="rounded-2xl border border-slate-800 bg-[#0A1124] p-6 sm:p-7" data-testid="demo-conversation-card">
            <div className="flex items-center justify-between border-b border-slate-800 pb-4">
              <span className="font-mono text-xs text-slate-500">glexis · conversation</span>
              <span className="rounded-full border border-amber-400/40 bg-amber-400/10 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-amber-300">
                Illustrative Conversation
              </span>
            </div>
            <div className="mt-5 space-y-4">
              {CONVERSATION.map((m, i) => {
                const isEmily = m.s === "Emily";
                return (
                  <div key={i} className={`flex ${isEmily ? "justify-start" : "justify-end"}`}>
                    <div className={`max-w-[85%] ${isEmily ? "" : "text-right"}`}>
                      <p className={`text-xs font-semibold ${isEmily ? "text-cyan-300" : "text-slate-300"}`}>{m.s}</p>
                      <div
                        className={`mt-1 rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                          isEmily
                            ? "rounded-tl-sm bg-slate-800/70 text-slate-200"
                            : "rounded-tr-sm bg-cyan-400/10 border border-cyan-400/20 text-slate-100"
                        }`}
                      >
                        {m.t}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>

          {/* Right — Interactive analysis */}
          <motion.div {...fade} className="rounded-2xl border border-slate-800 bg-[#0A1124] p-6 sm:p-7 min-h-[360px]">
            <AnimatePresence mode="wait">
              {!done ? (
                <motion.div
                  key="processing"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.4 }}
                  data-testid="demo-steps"
                >
                  <div className="flex items-center gap-2 border-b border-slate-800 pb-4">
                    <Loader2 className="h-4 w-4 animate-spin text-cyan-400" aria-hidden="true" />
                    <span className="text-sm text-slate-300">Analyzing conversation…</span>
                  </div>
                  <ul className="mt-5 space-y-3" aria-live="polite">
                    {STEPS.slice(0, visible).map((step, i) => (
                      <motion.li
                        key={step}
                        initial={{ opacity: 0, x: -8 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3 }}
                        className="flex items-center gap-3 text-sm text-slate-300"
                      >
                        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-cyan-400/15 text-cyan-400">
                          <Check className="h-3 w-3" aria-hidden="true" />
                        </span>
                        {step}…
                      </motion.li>
                    ))}
                  </ul>
                </motion.div>
              ) : (
                <motion.div
                  key="analysis"
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  data-testid="demo-analysis-card"
                >
                  <div className="flex items-center justify-between border-b border-slate-800 pb-4">
                    <span className="font-mono text-xs text-slate-500">glexis · analysis</span>
                    <span className="flex items-center gap-2 text-xs text-slate-500">
                      <span className="h-2 w-2 rounded-full bg-emerald-400" /> completed
                    </span>
                  </div>

                  <p className="mt-5 text-xs uppercase tracking-wide text-slate-500">Overall Communication Confidence</p>
                  <div className="flex items-baseline gap-2">
                    <span className="font-mono text-5xl font-bold text-cyan-400 gx-text-glow">84%</span>
                  </div>

                  <div className="mt-5 grid grid-cols-2 gap-3">
                    {METRICS.map(([l, v]) => (
                      <div key={l} className="rounded-lg border border-slate-800 bg-slate-900/40 p-3">
                        <p className="text-xs text-slate-500">{l}</p>
                        <p className="mt-1 font-mono text-sm font-semibold text-white">{v}</p>
                      </div>
                    ))}
                  </div>

                  <div className="mt-5 rounded-lg border border-slate-800 bg-slate-900/40 p-4">
                    <p className="text-xs uppercase tracking-wide text-slate-500 mb-2">What Glexis Observed</p>
                    <p className="text-sm leading-relaxed text-slate-300">
                      Timeline details changed as the conversation progressed. The speaker initially described
                      having spoken with the contractor, then clarified they had not spoken with them today, and
                      finally recalled that a brief conversation occurred after initially mentioning only a voicemail.
                    </p>
                    <p className="mt-2 text-sm leading-relaxed text-slate-400">
                      These observations do not indicate deception. They suggest that additional clarification or
                      context could improve understanding.
                    </p>
                  </div>

                  <div className="mt-4 grid gap-4 sm:grid-cols-2">
                    <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
                      <p className="text-xs uppercase tracking-wide text-slate-500 mb-3">Behavioral Patterns</p>
                      <ul className="space-y-2">
                        {PATTERNS.map((p) => (
                          <li key={p} className="flex items-start gap-2.5 text-sm text-slate-300">
                            <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-cyan-400" aria-hidden="true" />
                            <span className="leading-relaxed">{p}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
                      <p className="text-xs uppercase tracking-wide text-slate-500 mb-3">Suggested Follow-up Questions</p>
                      <ul className="space-y-2">
                        {QUESTIONS.map((q) => (
                          <li key={q} className="flex items-start gap-2.5 text-sm text-slate-300">
                            <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-cyan-400" aria-hidden="true" />
                            <span className="leading-relaxed">{q}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </div>

        {/* Educational callout */}
        <motion.div {...fade} className="mt-8 mx-auto max-w-4xl rounded-2xl border border-cyan-400/30 bg-cyan-400/[0.05] p-6 sm:p-8">
          <h3 className="font-display text-lg font-semibold text-white">
            Glexis helps people understand communication—not judge people.
          </h3>
          <p className="mt-3 text-sm leading-relaxed text-slate-300">
            Every conversation contains nuances, changing memories, and evolving context. Glexis highlights
            communication patterns to support better questions and more informed decisions. Results are analytical
            insights—not proof of deception or absolute truth.
          </p>
        </motion.div>

        {/* CTAs */}
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
          <Button asChild size="lg" className="h-12 px-8 bg-cyan-400 text-black font-semibold hover:bg-cyan-300 gx-glow">
            <Link to={primaryTo} data-testid="demo-try-cta">
              Try Glexis With My Conversation <ArrowRight className="h-4 w-4 ml-2" />
            </Link>
          </Button>
          <Button
            asChild
            size="lg"
            variant="outline"
            className="h-12 px-8 border-slate-700 bg-slate-900/40 text-white hover:bg-slate-800 hover:text-white"
          >
            <a href="#sample" data-testid="demo-download-sample">
              <Download className="h-4 w-4 mr-2" /> Download Sample Report
            </a>
          </Button>
        </div>
      </div>
    </section>
  );
}
